import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    TextField,
    Button,
    List,
    ListItem,
    ListItemText,
    ListItemIcon,
    IconButton,
    Paper,
    Avatar,
    LinearProgress,
    Chip,
    Divider,
    Tooltip,
    Grid,
    Badge
} from '@mui/material';
import {
    CloudUpload as CloudUploadIcon,
    Download as DownloadIcon,
    Image as ImageIcon,
    Description as DescriptionIcon,
    CheckCircle as CheckCircleIcon,
    Error as ErrorIcon,
    Delete as DeleteIcon,
    Close as CloseIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import axios from 'axios';
import { useHistory } from "react-router-dom";

const StyledPaper = styled(Paper)(({ theme }) => ({
    padding: theme.spacing(4),
    borderRadius: '16px',
    boxShadow: '0px 10px 30px rgba(0, 0, 0, 0.05)',
    background: 'white',
}));

const StyledUploadButton = styled(Button)(({ theme }) => ({
    padding: '10px 20px',
    textTransform: 'none',
    borderRadius: '8px',
    border: `2px dashed ${theme.palette.primary.light}`,
    '&:hover': {
        border: `2px dashed ${theme.palette.primary.main}`,
        backgroundColor: theme.palette.primary.light + '20',
    },
}));

const FilePreviewContainer = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(1.5),
    marginTop: theme.spacing(1),
    borderRadius: '8px',
    backgroundColor: theme.palette.grey[50],
    border: `1px solid ${theme.palette.grey[200]}`,
    position: 'relative',
    '&:hover': {
        backgroundColor: theme.palette.grey[100],
    },
}));

const RemoveFileButton = styled(IconButton)(({ theme }) => ({
    position: 'absolute',
    right: 4,
    top: 4,
    padding: '4px',
    backgroundColor: theme.palette.grey[300],
    '&:hover': {
        backgroundColor: theme.palette.grey[400],
    },
}));

const ConferenceOrganizerUpload = () => {
    const eventid = localStorage.getItem('eventid');
    const history = useHistory();
    const [title, setTitle] = useState('');
    const [files, setFiles] = useState([]);
    const [uploadedFiles, setUploadedFiles] = useState([]);
    const [isUploading, setIsUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [uploadStatus, setUploadStatus] = useState(null); // 'success' or 'error'
    const [selectedFilesCount, setSelectedFilesCount] = useState(0);

    // const fetchFiles = async () => {
    //     try {
    //         const res = await axios.get(`/api/speaker-documents/`);
    //         if (res.data.status === 200) {
    //             setUploadedFiles(res.data.documents);
    //         }
    //     } catch (err) {
    //         console.error('Error fetching documents', err);
    //     }
    // };

    const fetchFiles = async () => {
        try {
            const res = await axios.get('/api/org-get-speaker-docs', {
                params: { eventid: localStorage.getItem('eventid') }
            });

            if (res.data.status === 200) {
                setUploadedFiles(res.data.documents);
            }
        } catch (err) {
            console.error('Failed to fetch uploaded files', err);
        }
    };


    useEffect(() => {
        fetchFiles();
    }, []);

    const handleFileChange = (e) => {
        const newFiles = Array.from(e.target.files);
        setFiles(prevFiles => [...prevFiles, ...newFiles]);
        setSelectedFilesCount(prev => prev + newFiles.length);
    };

    const removeFile = (index) => {
        setFiles(prevFiles => prevFiles.filter((_, i) => i !== index));
        setSelectedFilesCount(prev => prev - 1);
    };
    const handleUpload = async () => {
        if (!title || files.length === 0) {
            setUploadStatus('error');
            setTimeout(() => setUploadStatus(null), 3000);
            return;
        }

        const formData = new FormData();
        formData.append('title', title);
        formData.append('eventid', eventid);
        files.forEach((file) => {
            formData.append('files[]', file); // correct key
        });

        try {
            setIsUploading(true);
            setUploadProgress(0);

            const res = await axios.post('/api/org-upload-speaker-doc', formData, {
                onUploadProgress: (progressEvent) => {
                    const percentCompleted = Math.round(
                        (progressEvent.loaded * 100) / progressEvent.total
                    );
                    setUploadProgress(percentCompleted);
                },
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });

            if (res.data.status === 200) {
                setUploadStatus('success');
                setTitle('');
                setFiles([]);
                setSelectedFilesCount(0);
                setTimeout(() => setUploadStatus(null), 3000);
                fetchFiles(); // refresh list
            }
        } catch (err) {
            if (err.response?.data?.errors) {
                const firstErrorField = Object.keys(err.response.data.errors)[0];
                const firstErrorMsg = err.response.data.errors[firstErrorField][0];
                alert(firstErrorMsg); // show exact Laravel error
            } else if (err.response?.data?.message) {
                alert(err.response.data.message);
            } else {
                alert('Upload failed, please try again.');
            }
            setUploadStatus('error');
            setTimeout(() => setUploadStatus(null), 3000);
        } finally {
            setIsUploading(false);
            setUploadProgress(0);
        }
    };


    const handleDelete = async (id) => {
        try {
            const res = await axios.delete(`/api/org-delete-speaker-doc/${id}`);
            if (res.data.status === 200) {
                fetchFiles();
            }
        } catch (err) {
            console.error('Error deleting document', err);
        }
    };

    const isImage = (filename) => /\.(jpg|jpeg|png|gif|webp)$/i.test(filename);
    const getFileType = (filename) => {
        if (isImage(filename)) return 'image';
        if (/\.(pdf)$/i.test(filename)) return 'pdf';
        if (/\.(docx?)$/i.test(filename)) return 'word';
        if (/\.(xlsx?)$/i.test(filename)) return 'excel';
        if (/\.(pptx?)$/i.test(filename)) return 'powerpoint';
        return 'file';
    };

    const getFileIcon = (filename) => {
        const type = getFileType(filename);
        switch (type) {
            case 'image': return <ImageIcon color="primary" />;
            case 'pdf': return <DescriptionIcon color="error" />;
            case 'word': return <DescriptionIcon color="info" />;
            case 'excel': return <DescriptionIcon color="success" />;
            case 'powerpoint': return <DescriptionIcon color="warning" />;
            default: return <DescriptionIcon />;
        }
    };

    const formatFileSize = (bytes) => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };

    return (
        <Box sx={{
            width: '100%', maxWidth: '900px',
            mx: 'auto',
            pt: 20,
            px: 3
        }}>
            <Box sx={{ width: '100%', maxWidth: '900px' }}>
                <Typography variant="h4" gutterBottom sx={{ fontWeight: 600, mb: 3, color: 'text.primary' }}>
                    Upload Documents For Speaker
                </Typography>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                    <Button
                        color="success"
                        size="small"
                        variant="contained"
                        onClick={() => history.goBack()}
                        sx={{ whiteSpace: 'nowrap' }}
                    >
                        Back
                    </Button>
                </Box>
                <StyledPaper elevation={0}>
                    <Typography variant="h6" gutterBottom sx={{ fontWeight: 500, mb: 2 }}>
                        Upload New Documents
                    </Typography>


                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
                        <TextField
                            label="Document Title"
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                            sx={{ flex: 1, minWidth: '250px' }}
                            variant="outlined"
                            size="small"
                            placeholder="Enter a title for all files"
                        />
                    </Box>

                    <Box sx={{ mb: 3 }}>
                        <Badge badgeContent={selectedFilesCount} color="primary" overlap="rectangular">
                            <StyledUploadButton
                                variant="outlined"
                                component="label"
                                startIcon={<CloudUploadIcon />}
                                // fullWidth
                                sx={{ flex: 1, minWidth: '450px' }}
                            >
                                Choose Files
                                <input
                                    type="file"
                                    hidden
                                    onChange={handleFileChange}
                                    multiple
                                />
                            </StyledUploadButton>
                        </Badge>
                        <br></br>
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                            You can upload multiple files at once. Supported formats: JPG, PNG, PDF, DOCX, XLSX, PPTX.
                            30MB max per file.
                        </Typography>
                        {files.length > 0 && (
                            <Grid container spacing={1} sx={{ mt: 2 }}>
                                {files.map((file, index) => (
                                    <Grid item xs={12} sm={6} key={index}>
                                        <FilePreviewContainer>
                                            <RemoveFileButton
                                                size="small"
                                                onClick={() => removeFile(index)}
                                                aria-label="remove file"
                                            >
                                                <CloseIcon fontSize="small" />
                                            </RemoveFileButton>
                                            <Avatar sx={{ bgcolor: 'primary.light', mr: 2, width: 40, height: 40 }}>
                                                {getFileIcon(file.name)}
                                            </Avatar>
                                            <Box sx={{ flexGrow: 1, overflow: 'hidden' }}>
                                                <Typography
                                                    variant="body2"
                                                    sx={{
                                                        fontWeight: 500,
                                                        whiteSpace: 'nowrap',
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis'
                                                    }}
                                                >
                                                    {file.name}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    {formatFileSize(file.size)}
                                                </Typography>
                                            </Box>
                                            <Chip
                                                label={getFileType(file.name).toUpperCase()}
                                                size="small"
                                                color="primary"
                                                variant="outlined"
                                                sx={{ ml: 1 }}
                                            />
                                        </FilePreviewContainer>
                                    </Grid>
                                ))}
                            </Grid>
                        )}
                    </Box>

                    {isUploading && (
                        <Box sx={{ width: '100%', mb: 2 }}>
                            <LinearProgress variant="determinate" value={uploadProgress} />
                            <Typography variant="caption" color="text.secondary">
                                Uploading: {uploadProgress}% ({files.length} files)
                            </Typography>
                        </Box>
                    )}

                    {uploadStatus === 'success' && (
                        <Box sx={{
                            display: 'flex',
                            alignItems: 'center',
                            backgroundColor: 'success.light',
                            color: 'success.dark',
                            p: 1,
                            borderRadius: 1,
                            mb: 2
                        }}>
                            <CheckCircleIcon sx={{ mr: 1 }} />
                            <Typography>Files uploaded successfully!</Typography>
                        </Box>
                    )}

                    {uploadStatus === 'error' && (
                        <Box sx={{
                            display: 'flex',
                            alignItems: 'center',
                            backgroundColor: 'error.light',
                            color: 'error.dark',
                            p: 1,
                            borderRadius: 1,
                            mb: 2
                        }}>
                            <ErrorIcon sx={{ mr: 1 }} />
                            <Typography>Please enter a title and choose at least one file</Typography>
                        </Box>
                    )}

                    <Button
                        variant="contained"
                        onClick={handleUpload}
                        disabled={isUploading || files.length === 0}
                        sx={{
                            borderRadius: '8px',
                            padding: '10px 24px',
                            textTransform: 'none',
                            fontWeight: 600,
                            boxShadow: 'none',
                            '&:hover': {
                                boxShadow: 'none',
                            },
                        }}
                    >
                        {isUploading ? `Uploading ${files.length} files...` : `Upload ${files.length} Files`}
                    </Button>
                </StyledPaper>

                <StyledPaper elevation={0} sx={{ mt: 4 }}>
                    <Typography variant="h6" gutterBottom sx={{ fontWeight: 500, mb: 2 }}>
                        Previously Uploaded Documents
                    </Typography>

                    {uploadedFiles.length > 0 ? (
                        <List sx={{ width: '100%' }}>
                            {uploadedFiles
                                .filter(doc => doc.title && doc.uploaded_file)  // filter here
                                .map((doc) => (
                                    <React.Fragment key={doc.id}>
                                        <ListItem
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'space-between',
                                                p: 2,
                                                '&:hover': {
                                                    backgroundColor: 'action.hover',
                                                    borderRadius: '8px',
                                                },
                                            }}
                                        >
                                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                <ListItemIcon>
                                                    {getFileIcon(doc.uploaded_file)}
                                                </ListItemIcon>
                                                <ListItemText
                                                    primary={
                                                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                                            {doc.title}
                                                        </Typography>
                                                    }
                                                    secondary={
                                                        <Typography variant="caption" color="text.secondary">
                                                            Uploaded on {new Date(doc.created_at).toLocaleDateString()}
                                                        </Typography>
                                                    }
                                                />
                                            </Box>

                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                {/* <Tooltip title="Download">
                                                    <IconButton
                                                        href={doc.uploaded_file_url}
                                                        download
                                                        target="_blank"
                                                        sx={{ color: 'primary.main' }}
                                                    >
                                                        <DownloadIcon />
                                                    </IconButton>
                                                </Tooltip> */}
                                                <Tooltip title="Download">
                                                    <IconButton
                                                        component="a"
                                                        href={doc.uploaded_file_url}
                                                        download={doc.uploaded_file} // <-- use original name here
                                                        target="_blank"
                                                        sx={{ color: 'primary.main' }}
                                                    >
                                                        <DownloadIcon />
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title="Delete">
                                                    <IconButton
                                                        onClick={() => handleDelete(doc.id)}
                                                        sx={{ color: 'error.main' }}
                                                    >
                                                        <DeleteIcon />
                                                    </IconButton>
                                                </Tooltip>
                                            </Box>
                                        </ListItem>

                                        {isImage(doc.uploaded_file) && (
                                            <Box
                                                component="img"
                                                src={doc.uploaded_file_url}
                                                alt={doc.title}
                                                sx={{
                                                    maxWidth: '300px',
                                                    maxHeight: '300px',
                                                    mt: 1,
                                                    mb: 2,
                                                    ml: 8,
                                                    borderRadius: '8px',
                                                    boxShadow: 1,
                                                }}
                                            />
                                        )}

                                        <Divider sx={{ my: 1 }} />
                                    </React.Fragment>
                                ))}
                        </List>
                    ) : (
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                py: 4,
                                color: 'text.secondary',
                            }}
                        >
                            <DescriptionIcon sx={{ fontSize: 48, mb: 1, opacity: 0.5 }} />
                            <Typography>No documents uploaded yet</Typography>
                            <Typography variant="caption">Upload your first documents above</Typography>
                        </Box>
                    )}

                </StyledPaper>
            </Box>
        </Box>
    );
};

export default ConferenceOrganizerUpload;